/**
 * Weather Store for Bonapisca
 * Manages weather data and fishing conditions.
 */

import { create } from 'zustand';
import i18next from 'i18next';
import { persist } from 'zustand/middleware';
import type { WeatherConditions, DailyForecast, FishingScore } from '../types/weather';
import { weatherService } from '../services/weatherService';

const tWeatherStore = (key: string, options?: Record<string, unknown>) =>
  String(i18next.t(`toolbox:stores.weather.${key}`, options));

const isExpectedLocationError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes(String(i18next.t('toolbox:services.weather.errors.locationDeniedPrefix')));
};

interface WeatherState {
  currentWeather: WeatherConditions | null;
  forecast: DailyForecast[];
  marineConditions: {
    waves: { height: number; period: number; direction: number; timestamp: Date }[];
    wind: { speed: number; direction: number; gust?: number }[];
  } | null;
  fishingScore: FishingScore | null;
  currentPosition: { lat: number; lon: number } | null;
  currentPositionSource: 'device' | 'regional_reference' | null;
  isLoading: boolean;
  isLoadingForecast: boolean;
  isLoadingMarine: boolean;
  error: string | null;
  lastUpdated: Date | null;
  fetchCurrentWeather: (lat?: number, lon?: number, locale?: string) => Promise<void>;
  fetchForecast: (lat?: number, lon?: number, locale?: string) => Promise<void>;
  fetchMarineConditions: (lat?: number, lon?: number) => Promise<void>;
  updateLocation: (lat: number, lon: number, source: 'device' | 'regional_reference') => void;
  requestDeviceLocation: () => Promise<{ lat: number; lon: number }>;
  clearError: () => void;
  calculateFishingConditions: () => void;
}

const samePosition = (
  left: { lat: number | undefined; lon: number | undefined },
  right: { lat: number; lon: number } | null
) => Boolean(
  right &&
  typeof left.lat === 'number' &&
  typeof left.lon === 'number' &&
  Math.abs(left.lat - right.lat) < 0.0001 &&
  Math.abs(left.lon - right.lon) < 0.0001
);

export const useWeatherStore = create<WeatherState>()(
  persist(
    (set, get) => ({
      currentWeather: null,
      forecast: [],
      marineConditions: null,
      fishingScore: null,
      currentPosition: null,
      currentPositionSource: null,
      isLoading: false,
      isLoadingForecast: false,
      isLoadingMarine: false,
      error: null,
      lastUpdated: null,

      fetchCurrentWeather: async (lat?: number, lon?: number, locale = 'en') => {
        set({ isLoading: true, error: null });

        try {
          let position = { lat, lon };
          if (lat == null || lon == null) {
            const currentPos = get().currentPosition;
            if (currentPos) {
              position = currentPos;
            } else {
              const newPos = await weatherService.getCurrentPosition();
              position = newPos;
              get().updateLocation(newPos.lat, newPos.lon, 'device');
            }
          }

          const weather = await weatherService.getCurrentWeather(position.lat!, position.lon!, locale);
          if (!samePosition(position, get().currentPosition)) {
            return;
          }
          const derivedMarine = weather.marine
            ? {
                waves: [{
                  height: weather.marine.waveHeight ?? 0,
                  period: weather.marine.wavePeriod ?? 0,
                  direction: weather.marine.waveDirection ?? 0,
                  timestamp: weather.timestamp
                }],
                wind: [{
                  speed: weather.windSpeed ?? 0,
                  direction: weather.windDirection ?? 0,
                  gust: weather.windGust
                }]
              }
            : null;

          set({
            currentWeather: weather,
            marineConditions: derivedMarine,
            fishingScore: weatherService.calculateFishingScore(
              weather,
              derivedMarine?.waves?.[0] ? { height: derivedMarine.waves[0].height } : undefined
            ),
            lastUpdated: new Date(),
            isLoading: false,
            error: null
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : tWeatherStore('weatherFetchFailed');

          if (isExpectedLocationError(error)) {
            console.info('Weather unavailable without location access:', message);
          } else {
            console.error('Failed to fetch weather:', error);
          }

          if (samePosition({ lat, lon }, get().currentPosition) || (lat == null && lon == null)) {
            set({ error: message, isLoading: false });
          }
        }
      },

      fetchForecast: async (lat?: number, lon?: number, locale = 'en') => {
        set({ isLoadingForecast: true, error: null });

        try {
          let position = { lat, lon };
          if (lat == null || lon == null) {
            const currentPos = get().currentPosition;
            if (currentPos) {
              position = currentPos;
            } else {
              const newPos = await weatherService.getCurrentPosition();
              position = newPos;
              get().updateLocation(newPos.lat, newPos.lon, 'device');
            }
          }

          const forecast = await weatherService.getForecast(position.lat!, position.lon!, locale);
          if (!samePosition(position, get().currentPosition)) {
            return;
          }
          set({ forecast, isLoadingForecast: false, error: null });
        } catch (error) {
          const message = error instanceof Error ? error.message : tWeatherStore('forecastFetchFailed');

          if (isExpectedLocationError(error)) {
            console.info('Forecast unavailable without location access:', message);
          } else {
            console.error('Failed to fetch forecast:', error);
          }

          if (samePosition({ lat, lon }, get().currentPosition) || (lat == null && lon == null)) {
            set({ error: message, isLoadingForecast: false });
          }
        }
      },

      fetchMarineConditions: async (lat?: number, lon?: number) => {
        set({ isLoadingMarine: true, error: null });
        let position = { lat, lon };

        try {
          if (lat == null || lon == null) {
            const currentPos = get().currentPosition;
            if (currentPos) {
              position = currentPos;
            } else {
              const newPos = await weatherService.getCurrentPosition();
              position = newPos;
              get().updateLocation(newPos.lat, newPos.lon, 'device');
            }
          }

          const marine = await weatherService.getMarineForecast(position.lat!, position.lon!);
          if (!samePosition(position, get().currentPosition)) {
            return;
          }
          set({ marineConditions: marine, isLoadingMarine: false, error: null });
          get().calculateFishingConditions();
        } catch (error) {
          console.error('Failed to fetch marine conditions:', error);
          if (samePosition(position, get().currentPosition) || (position.lat == null && position.lon == null)) {
            set({
              error: error instanceof Error ? error.message : tWeatherStore('marineFetchFailed'),
              isLoadingMarine: false
            });
          }
        }
      },

      updateLocation: (lat: number, lon: number, source: 'device' | 'regional_reference') => set((state) => {
        const changed = !samePosition({ lat, lon }, state.currentPosition) || state.currentPositionSource !== source;
        return {
          currentPosition: { lat, lon },
          currentPositionSource: source,
          ...(changed ? {
            currentWeather: null,
            forecast: [],
            marineConditions: null,
            fishingScore: null,
            isLoading: false,
            isLoadingForecast: false,
            isLoadingMarine: false,
            lastUpdated: null,
            error: null
          } : {})
        };
      }),
      requestDeviceLocation: async () => {
        try {
          const position = await weatherService.getCurrentPosition();
          get().updateLocation(position.lat, position.lon, 'device');
          return position;
        } catch (error) {
          set({ error: error instanceof Error ? error.message : tWeatherStore('weatherFetchFailed') });
          throw error;
        }
      },
      clearError: () => set({ error: null }),

      calculateFishingConditions: () => {
        const { currentWeather, marineConditions } = get();
        if (!currentWeather) return;

        const currentWaves = marineConditions?.waves?.[0];
        const score = weatherService.calculateFishingScore(
          currentWeather,
          currentWaves ? { height: currentWaves.height } : undefined
        );

        set({ fishingScore: score });
      }
    }),
    {
      name: 'weather-store',
      version: 1,
      migrate: (persistedState) => {
        const state = persistedState as Partial<WeatherState>;
        const keepRegional = state.currentPositionSource === 'regional_reference';
        return {
          ...state,
          currentPosition: keepRegional ? state.currentPosition || null : null,
          currentPositionSource: keepRegional ? 'regional_reference' : null,
          lastUpdated: null
        };
      },
      partialize: (state) => ({
        currentPosition: state.currentPositionSource === 'regional_reference' ? state.currentPosition : null,
        currentPositionSource: state.currentPositionSource === 'regional_reference' ? 'regional_reference' : null,
        lastUpdated: null
      })
    }
  )
);

export const useCurrentWeather = () => useWeatherStore((state) => state.currentWeather);
export const useWeatherForecast = () => useWeatherStore((state) => state.forecast);
export const useMarineConditions = () => useWeatherStore((state) => state.marineConditions);
export const useFishingScore = () => useWeatherStore((state) => state.fishingScore);
export const useWeatherLoading = () => useWeatherStore((state) => ({
  isLoading: state.isLoading,
  isLoadingForecast: state.isLoadingForecast,
  isLoadingMarine: state.isLoadingMarine
}));
